import React from 'react'
import { renderToString } from 'react-dom/server'
import mapboxgl from 'mapbox-gl'
import Supercluster from 'supercluster'
import { UnifiedRoute } from '../../../services/firebase/routes'
import { UnifiedRouteRenderer } from '../../../utils/unifiedRouteRenderer'
import {
  getTotalDistance,
  getTotalElevationGain,
  calculateUnpavedPercentage,
  getRouteTypeDisplay,
  getRouteStageCount
} from '../../../utils/routeDisplay'
import { Tent, Calendar, Route, Trophy, Mountain } from 'lucide-react'

interface CreateClusterMarkerParams {
  cluster: any
  map: mapboxgl.Map
  clusterRef: React.MutableRefObject<Supercluster | null>
  publishedRoutes: UnifiedRoute[]
  unifiedRenderer: UnifiedRouteRenderer | null
  onRouteSelect: (route: any) => void
  selectedRoute: any | null
  markersRef: React.MutableRefObject<mapboxgl.Marker[]>
  activeHoverRef: React.MutableRefObject<string | null>
  setHoveredRouteId: (id: string | null) => void
  onLazyLoadRoute?: (routeId: string) => Promise<any>
}

export function createClusterMarker({
  cluster,
  map,
  clusterRef,
  publishedRoutes,
  unifiedRenderer,
  onRouteSelect,
  selectedRoute,
  markersRef,
  activeHoverRef,
  setHoveredRouteId,
  onLazyLoadRoute
}: CreateClusterMarkerParams) {
  const [lng, lat] = cluster.geometry.coordinates
  const count = cluster.properties?.point_count
  
  if (!count) return
  
  const supportsHover = typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches

  // Create cluster element
  const clusterElement = document.createElement('div')
  clusterElement.className = 'route-cluster-marker'
  clusterElement.innerHTML = `
    <div class="cluster-container" style="
      position: relative;
      width: 32px;
      height: 32px;
      cursor: pointer;
      opacity: 0.9;
    ">
      <div class="cluster-circle" style="
        width: 32px;
        height: 32px;
        background: linear-gradient(135deg, rgba(0,0,0,0.6) 0%, rgba(31,41,55,0.7) 100%);
        backdrop-filter: blur(8px);
        -webkit-backdrop-filter: blur(8px);
        border: 1.5px solid rgba(255,255,255,0.08);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 4px 12px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05);
        transition: all 0.2s ease;
      ">
        <span style="
          color: white;
          font-weight: 600;
          font-size: 12px;
        ">${count}</span>
      </div>
    </div>
  `
  
  // Safety check - ensure map still exists and has a canvas container
  if (!map || typeof map.getCanvasContainer !== 'function') {
    console.warn('⚠️ Map instance is invalid, skipping cluster marker creation')
    return
  }
  
  let clusterMarker: mapboxgl.Marker | null = null
  try {
    clusterMarker = new mapboxgl.Marker({
      element: clusterElement
    })
      .setLngLat([lng, lat])
      .addTo(map)
  } catch (error) {
    console.error('❌ Failed to create cluster marker:', error)
    clusterElement.remove()
    return
  }
  
  // Hover state
  let hoverPanel: HTMLDivElement | null = null
  let hoverTimeout: NodeJS.Timeout | null = null
  let isMouseOverPanel = false
  let isMouseOverMarker = false
  
  const hidePanel = () => {
    if (!supportsHover) {
      return
    }
    if (hoverTimeout) clearTimeout(hoverTimeout)
    hoverTimeout = setTimeout(() => {
      if (!isMouseOverPanel && !isMouseOverMarker && hoverPanel) {
        hoverPanel.style.display = 'none'
        
        // Clear rendered routes when hiding panel
        if (unifiedRenderer) {
          console.log('🧹 Clearing cluster hover routes')
          unifiedRenderer.clearHoveredRoute()
        }
      }
    }, 200)
  }
  
  const createHoverPanel = () => {
    if (!supportsHover) {
      return
    }
    if (hoverPanel) {
      hoverPanel.remove()
    }
    
    // Get routes in cluster (limit to 5 for performance)
    const clusterRoutes = clusterRef.current?.getLeaves(cluster.properties!.cluster_id, 5)
      .map((leaf: any) => {
        const routeIndex = leaf.properties?.routeIndex
        return routeIndex !== undefined ? publishedRoutes[routeIndex] : null
      })
      .filter(Boolean) || []
    
    // Don't render all routes here - we'll render individual routes on hover
    
    hoverPanel = document.createElement('div')
    hoverPanel.className = 'route-cluster-hover-panel'
    hoverPanel.style.cssText = `
      position: fixed;
      background: linear-gradient(to bottom, rgba(0,0,0,0.6), rgba(31,41,55,0.7));
      border: 1px solid rgba(255, 255, 255, 0.06);
      border-radius: 12px;
      padding: 8px;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255,255,255,0.05);
      z-index: 1000;
      display: none;
      pointer-events: auto;
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
    `
    
    const previewContent = document.createElement('div')
    previewContent.style.cssText = 'display: flex; flex-direction: column; gap: 8px;'
    
    clusterRoutes.forEach((route: UnifiedRoute) => {
      const routePreview = createRoutePreviewElement(route, map, onRouteSelect, unifiedRenderer, onLazyLoadRoute)
      previewContent.appendChild(routePreview)
    })
    
    hoverPanel.appendChild(previewContent)
    document.body.appendChild(hoverPanel)
    
    // Panel event handlers
    hoverPanel.addEventListener('mouseenter', () => {
      isMouseOverPanel = true
      if (hoverTimeout) clearTimeout(hoverTimeout)
    })
    
    hoverPanel.addEventListener('mouseleave', () => {
      isMouseOverPanel = false
      hidePanel()
    })
  }
  
  // Cluster event handlers
  clusterElement.addEventListener('mouseenter', () => {
    if (!supportsHover) {
      return
    }
    isMouseOverMarker = true
    const circle = clusterElement.querySelector('.cluster-circle') as HTMLElement
    if (circle) {
      circle.style.background = 'linear-gradient(135deg, rgba(16,185,129,0.3) 0%, rgba(5,150,105,0.4) 100%)'
      circle.style.backdropFilter = 'blur(12px)'
      circle.style.webkitBackdropFilter = 'blur(12px)'
      circle.style.border = '1.5px solid rgba(16,185,129,0.3)'
      circle.style.boxShadow = '0 6px 20px rgba(16,185,129,0.3), inset 0 1px 0 rgba(255,255,255,0.1)'
      circle.style.transform = 'scale(1.1)'
    }
    
    if (!hoverPanel) {
      createHoverPanel()
    }
    
    if (hoverPanel) {
      const rect = clusterElement.getBoundingClientRect()
      hoverPanel.style.display = 'block'
      
      const panelHeight = hoverPanel.offsetHeight
      let top = rect.top - panelHeight - 10
      let left = rect.left + rect.width / 2 - hoverPanel.offsetWidth / 2
      
      if (top < 10) {
        top = rect.bottom + 10
      }
      if (left < 10) {
        left = 10
      } else if (left + hoverPanel.offsetWidth > window.innerWidth - 10) {
        left = window.innerWidth - hoverPanel.offsetWidth - 10
      }
      
      hoverPanel.style.top = `${top}px`
      hoverPanel.style.left = `${left}px`
    }
  })

  clusterElement.addEventListener('mouseleave', () => {
    if (!supportsHover) {
      return
    }
    isMouseOverMarker = false
    const circle = clusterElement.querySelector('.cluster-circle') as HTMLElement
    if (circle) {
      circle.style.background = 'linear-gradient(135deg, rgba(0,0,0,0.6) 0%, rgba(31,41,55,0.7) 100%)'
      circle.style.backdropFilter = 'blur(8px)'
      circle.style.webkitBackdropFilter = 'blur(8px)'
      circle.style.border = '1.5px solid rgba(255,255,255,0.08)'
      circle.style.boxShadow = '0 4px 12px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05)'
      circle.style.transform = 'scale(1)'
    }
    hidePanel()
  })
  
  clusterElement.addEventListener('click', (e) => {
    e.stopPropagation()
    if (hoverPanel) hoverPanel.style.display = 'none'
    const expansionZoom = clusterRef.current?.getClusterExpansionZoom(cluster.properties!.cluster_id)
    if (expansionZoom) {
      map.easeTo({
        center: [lng, lat],
        zoom: expansionZoom
      })
    }
  })
  
  // Clean up on removal
  ;(clusterMarker as any).remove = (() => {
    const originalRemove = clusterMarker.remove.bind(clusterMarker)
    return () => {
      if (hoverPanel) {
        hoverPanel.remove()
        hoverPanel = null
      }
      // Clear any rendered routes
      if (unifiedRenderer) {
        unifiedRenderer.clearHoveredRoute()
      }
      originalRemove()
    }
  })()

  if (clusterMarker) {
    markersRef.current.push(clusterMarker)
  }
}

// Helper function to create route preview element
function createRoutePreviewElement(
  route: UnifiedRoute, 
  map: mapboxgl.Map,
  onRouteSelect: (route: any) => void,
  unifiedRenderer: UnifiedRouteRenderer | null,
  onLazyLoadRoute?: (routeId: string) => Promise<any>
): HTMLDivElement {
  const routePreview = document.createElement('div')
  routePreview.className = 'route-preview-item'
  routePreview.style.cssText = `
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
    background: rgba(255, 255, 255, 0.03);
  `
  
  // Route type icon
  const iconContainer = document.createElement('div')
  iconContainer.style.cssText = `
    width: 32px;
    height: 32px;
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  `
  
  const routeTypeDisplay = getRouteTypeDisplay(route.routeType)
  const iconComponent = (() => {
    switch (routeTypeDisplay) {
      case 'Bikepacking':
        return <Tent size={16} stroke="white" strokeWidth={2.5} />
      case 'Event':
        return <Calendar size={16} stroke="white" strokeWidth={2.5} />
      case 'Permanent':
        return <Trophy size={16} stroke="white" strokeWidth={2.5} />
      default:
        return <Route size={16} stroke="white" strokeWidth={2.5} />
    }
  })()
  iconContainer.innerHTML = renderToString(iconComponent)
  
  // Route info
  const infoContainer = document.createElement('div')
  infoContainer.style.cssText = 'flex: 1; min-width: 0;'
  
  const routeName = document.createElement('div')
  routeName.textContent = route.name
  routeName.style.cssText = `
    font-size: 13px;
    font-weight: 600;
    color: white;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 200px;
  `
  
  // Stats
  const routeStats = document.createElement('div')
  routeStats.style.cssText = `
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 11px;
    color: #9ca3af;
    margin-top: 2px;
  `
  
  const distance = getTotalDistance(route)
  const formattedDistance = Math.ceil(distance).toLocaleString()
  const elevationGain = getTotalElevationGain(route)
  const unpavedPercentage = calculateUnpavedPercentage(route)
  const stagesCount = getRouteStageCount(route)
  
  routeStats.innerHTML = `
    <div style="display: flex; align-items: center; gap: 4px;">
      ${renderToString(<Route className="w-2.5 h-2.5 text-emerald-400" />)}
      <span>${formattedDistance}km</span>
    </div>
    <div style="display: flex; align-items: center; gap: 4px;">
      ${renderToString(<Mountain className="w-2.5 h-2.5 text-emerald-400" />)}
      <span>${elevationGain.toLocaleString()}m</span>
    </div>
    <div style="display: flex; align-items: center; gap: 4px;">
      <svg class="w-2.5 h-2.5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
      <span>${unpavedPercentage}% unp.</span>
    </div>
    ${stagesCount > 1 ? `
      <div style="display: flex; align-items: center; gap: 4px;">
        <svg class="w-2.5 h-2.5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
        </svg>
        <span>${stagesCount} stages</span>
      </div>
    ` : ''}
  `
  
  infoContainer.appendChild(routeName)
  infoContainer.appendChild(routeStats)
  
  routePreview.appendChild(iconContainer)
  routePreview.appendChild(infoContainer)
  
  // Hover effect with route rendering
  routePreview.addEventListener('mouseenter', async () => {
    routePreview.style.background = 'rgba(16, 185, 129, 0.1)'
    
    // Check if route needs to be lazy loaded
    let routeToRender = route
    if (onLazyLoadRoute && route.id) {
      // Check if route has full coordinate data
      const hasFullData = (route.coordinates && route.coordinates.length > 1) || 
                         (route.routes && route.routes[0]?.coordinates?.length > 1)
      
      if (!hasFullData) {
        console.log(`🔄 Lazy loading route ${route.name} for hover`)
        const fullRoute = await onLazyLoadRoute(route.id)
        if (fullRoute) {
          routeToRender = fullRoute
        }
      }
    }
    
    // Render the route on the map
    if (unifiedRenderer && routeToRender.id) {
      console.log(`🗺️ Rendering route ${routeToRender.name} on hover`)
      unifiedRenderer.renderHoveredRoute(routeToRender)
    }
  })
  
  routePreview.addEventListener('mouseleave', () => {
    routePreview.style.background = 'rgba(255, 255, 255, 0.03)'
    
    // Clear the rendered route
    if (unifiedRenderer) {
      console.log(`🧹 Clearing hovered route ${route.name}`)
      unifiedRenderer.clearHoveredRoute()
    }
  })
  
  // Click handler
  routePreview.addEventListener('click', () => {
    // Show RoutePopupCard without panning
    onRouteSelect({
      id: route.id,
      name: route.name,
      routeType: getRouteTypeDisplay(route.routeType),
      coordinates: route.coordinates || [],
      location: route.location || '',
      region: route.region || '',
      distance: getTotalDistance(route),
      elevationGain: getTotalElevationGain(route),
      surface: `${100 - calculateUnpavedPercentage(route)}% paved`,
      unpavedPercentage: calculateUnpavedPercentage(route),
      description: route.description || '',
      creator: route.attribution?.name || route.ownerDisplayName || route.ownerId,
      creatorPhoto: route.ownerPhoto || null,
      organization: route.submission?.clubName || 'Personal Route',
      clubLogo: (route as any).clubLogo || null,
      attribution: route.attribution,
      images: [],
      thumbnail: route.thumbnail || null,
      firebaseRoute: route
    })
  })
  
  return routePreview
}
